import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CgMenuGridR } from "react-icons/cg";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../Assets/Logos/maptech.webp";
import servicesData from "../../context/Services.json";

// ServiceIcon component for displaying proper icons
const ServiceIcon = ({ category }) => {
  const getIconUrl = (category) => {
    const service = servicesData.services.find(s => s.category === category);
    return service?.icon;
  };

  const iconUrl = getIconUrl(category);
  
  if (iconUrl) {
    return (
      <img 
        src={iconUrl} 
        alt={`${category} icon`}
        className="w-8 h-8 object-contain"
      />
    );
  }
  
  // Fallback to first letter if no icon
  return (
    <span className="text-brandBlue text-xs font-bold">
      {category.charAt(0)}
    </span>
  );
};

const SecondaryNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activePath, setActivePath] = useState("/");
  const navigate = useNavigate();
  const location = useLocation();

  // Update active path when location changes
  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);

  // Extract service categories from JSON data
  const getServiceCategories = () => {
    const categories = {};
    servicesData.services.forEach(service => {
      // Create SEO-friendly URLs
      // const slug = service.category.toLowerCase().replace(/\s+/g, '-');
      const slug = service.categoryId;
      categories[service.category] = `/services/${slug}`;
    });
    return categories;
  };

  // Navigation data with services from JSON
  const navigation = {
    "About Us": "/about",
    Gallery: "/gallery",
    Blogs: "/blogs",
    Services: getServiceCategories(),
    Projects: "/projects",
    "Contact Us": "/contact",
  };

  const toggleDropdown = (item) =>
    setActiveDropdown(activeDropdown === item ? null : item);

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
    setActiveDropdown(null);
  };

  // Check if a path is active
  const isActive = (path) => {
    if (path === "/") {
      return activePath === "/";
    }
    return activePath.startsWith(path);
  };

  // Check if any service subpage is active
  const isServicesActive = () => {
    return Object.values(navigation.Services).some(servicePath => 
      activePath.startsWith(servicePath)
    );
  };

  // Get service description
  const getServiceDescription = (category) => {
    const service = servicesData.services.find(s => s.category === category);
    return service?.description || "Professional services for your business needs.";
  };

  return (
    <>
      {/* Navbar with white background */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md border-b border-gray-200"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex-shrink-0 flex items-center cursor-pointer"
              onClick={() => handleNavigate("/")}
            >
              <img
                src={logo}
                alt="Maptech Logo"
                className="h-14 w-auto lg:h-16"
              />
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {Object.entries(navigation).map(([key, value]) => (
                <div
                  key={key}
                  className="relative"
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {typeof value === "object" ? (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      onMouseEnter={() => setActiveDropdown(key)}
                      onClick={() => toggleDropdown(key)}
                      className={`px-5 py-2 font-medium text-sm tracking-wide flex items-center space-x-2 rounded-lg transition-all duration-300 ${
                        isServicesActive()
                          ? "text-brandBlue bg-blue-50 border border-blue-100 font-semibold"
                          : "text-gray-700 hover:text-brandBlue hover:bg-gray-100"
                      }`}
                    >
                      <span>{key}</span>
                      <motion.svg
                        animate={{ rotate: activeDropdown === key ? 180 : 0 }}
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </motion.svg>
                    </motion.button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      onClick={() => handleNavigate(value)}
                      className={`px-5 py-2 font-medium text-sm tracking-wide rounded-lg transition-all duration-300 ${
                        isActive(value)
                          ? "text-brandBlue bg-blue-50 border border-blue-100 font-semibold"
                          : "text-gray-700 hover:text-brandBlue hover:bg-gray-100"
                      }`}
                    >
                      {key}
                    </motion.button>
                  )}

                  {/* Dropdown */}
                  <AnimatePresence>
                    {activeDropdown === key && typeof value === "object" && (
                      <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 15 }}
                        className="absolute top-full left-0 w-80 rounded-2xl shadow-2xl overflow-hidden bg-white border border-gray-200 z-50"
                      >
                        <div className="p-3 space-y-2">
                          {Object.entries(value).map(([subKey, path]) => (
                            <button
                              key={subKey}
                              onClick={() => handleNavigate(path)}
                              className={`block w-full text-left px-4 py-3 text-sm rounded-lg transition-all duration-300 group ${
                                isActive(path)
                                  ? "text-brandBlue bg-blue-50 border border-blue-100 font-semibold"
                                  : "text-gray-700 hover:text-brandBlue hover:bg-gray-100"
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 flex items-center justify-center bg-brandBlue/10 rounded-lg group-hover:bg-brandBlue/20 transition-colors">
                                  <ServiceIcon category={subKey} />
                                </div>
                                <div className="flex-1 text-left">
                                  <div className="font-semibold text-sm">{subKey}</div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {getServiceDescription(subKey).substring(0, 65)}...
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* Menu Buttons */}
            <div className="flex items-center space-x-2">
              {/* Desktop Menu Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsOpen(!isOpen)}
                className={`hidden lg:flex items-center space-x-2 px-6 py-3 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 shadow-md hover:shadow-lg ${
                  isActive("/contact")
                    ? "bg-green-500 text-white hover:bg-green-600"
                    : "bg-brandBlue text-white hover:bg-brandBlue/90"
                }`}
              >
                <span>{isActive("/contact") ? "Active" : "Menu"}</span>
                <CgMenuGridR size={20} />
              </motion.button>

              {/* Mobile Menu Button */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden w-12 h-12 flex items-center justify-center rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-300"
              >
                <CgMenuGridR size={24} />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Sidebar Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />

            {/* Sidebar Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 w-80 max-w-full bg-white shadow-2xl z-50 overflow-y-auto"
            >
              <div className="p-6 h-full flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                  <img src={logo} alt="Maptech Logo" className="h-10 w-auto" />
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsOpen(false)}
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-300"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </motion.button>
                </div>

                {/* Navigation */}
                <div className="space-y-1 flex-1">
                  {Object.entries(navigation).map(([key, value]) => (
                    <div
                      key={key}
                      className="border-b border-gray-200 last:border-b-0"
                    >
                      {typeof value === "object" ? (
                        <>
                          <motion.button
                            whileTap={{ scale: 0.98 }}
                            onClick={() => toggleDropdown(key)}
                            className={`w-full flex items-center justify-between py-4 text-lg px-3 rounded-lg transition-all duration-300 ${
                              isServicesActive()
                                ? "text-brandBlue bg-blue-50 border-r-4 border-brandBlue font-semibold"
                                : "text-gray-700 font-medium hover:bg-gray-100"
                            }`}
                          >
                            <span>{key}</span>
                            <motion.svg
                              animate={{
                                rotate: activeDropdown === key ? 180 : 0,
                              }}
                              className="w-5 h-5 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </motion.svg>
                          </motion.button>

                          <AnimatePresence>
                            {activeDropdown === key && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="ml-4 pb-3 space-y-2"
                              >
                                {Object.entries(value).map(([subKey, path]) => (
                                  <button
                                    key={subKey}
                                    onClick={() => handleNavigate(path)}
                                    className={`block w-full text-left py-3 px-4 text-base rounded-lg transition-all duration-300 border-l-4 group ${
                                      isActive(path)
                                        ? "text-brandBlue bg-blue-50 border-brandBlue font-semibold"
                                        : "text-gray-600 hover:text-brandBlue hover:bg-gray-50 border-gray-300 hover:border-brandBlue"
                                    }`}
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className="w-10 h-10 flex items-center justify-center bg-brandBlue/10 rounded-lg group-hover:bg-brandBlue/20 transition-colors">
                                        <ServiceIcon category={subKey} />
                                      </div>
                                      <div className="flex-1 text-left">
                                        <div className="font-medium">{subKey}</div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          {getServiceDescription(subKey).substring(0, 50)}...
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        <button
                          onClick={() => handleNavigate(value)}
                          className={`block w-full text-left py-4 px-3 text-lg rounded-lg transition-all duration-300 ${
                            isActive(value)
                              ? "text-brandBlue bg-blue-50 border-r-4 border-brandBlue font-semibold"
                              : "text-gray-700 font-medium hover:text-brandBlue hover:bg-gray-100"
                          }`}
                        >
                          {key}
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-4 font-semibold text-lg tracking-wide rounded-xl transition-all duration-300 mt-6 mb-4 shadow-lg hover:shadow-xl ${
                    isActive("/contact")
                      ? "bg-green-500 text-white hover:bg-green-600"
                      : "bg-brandBlue text-white hover:bg-brandBlue/90"
                  }`}
                  onClick={() => handleNavigate("/contact")}
                >
                  {isActive("/contact") ? "Currently Viewing" : "Get Started"}
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer */}
      <div className="h-16 lg:h-20" />
    </>
  );
};

export default SecondaryNavbar;